import React from "react";
import { useState, useRef, useEffect } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import GuideProfileGraph from "../../components/guideProfileGraph/guideProfileGraph";
import { categorizeReviews } from "../../utility/categorizedReviews";
import moment from "moment";
import "./guideProfile.css";
import ThreeMonthGuide from "../../components/threeMonthGuide/threeMonthGuide";

// Function to clean language names
const cleanName = (name) => {
  const s = name == null ? "" : String(name);
  return s
    .trim()
    .replace(/\r|\n/g, "")
    .replace(/\s{2,}/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const starIcons = (rating) => {
  return Array(5)
    .fill()
    .map((_, i) => (
      <span key={i} className={i < rating ? "star-filled" : "star-empty"}>
        ★
      </span>
    ));
};

const GuideProfile = () => {
  const { guideName } = useParams();

  const reviews = useSelector((state) => state.reviews.reviews);
  const guideContacts = useSelector((state) => state.guideContacts.data);

  const matchingGuide = guideContacts.find(
    (guide) => cleanName(guide["Guide Name"]) === guideName
  );

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [languageFilter, setLanguageFilter] = useState("All");
  const [starRatingFilter, setStarRatingFilter] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const reviewsPerPage = 10;
  const [reviewYearFilter, setReviewYearFilter] = useState("All");
  const [productCodeFilter, setProductCodeFilter] = useState("All");
  const ProductCodesWithOptions = ["UKLO050", "FRPA049"];
  const [isYearDropdownOpen, setIsYearDropdownOpen] = useState(false);
  const yearDropdownRef = React.useRef(null);

  const cleanedGuideName = cleanName(guideName);
  const guideReviews = reviews.filter(
    (review) => cleanName(review[10]) === cleanedGuideName
  );

  const categorizedReviews = categorizeReviews(guideReviews);

  useEffect(() => {
  function handleClickOutside(event) {
    if (
      yearDropdownRef.current &&
      !yearDropdownRef.current.contains(event.target)
    ) {
      setIsYearDropdownOpen(false);
    }
  }

  document.addEventListener("mousedown", handleClickOutside);

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, []);

const toggleYear = (year) => {
  if (year === "All") {
    setYearFilter(["All"]);
    return;
  }

  let updated = [...yearFilter];

  // remove "All" if selecting specific year
  updated = updated.filter((y) => y !== "All");

  if (updated.includes(year)) {
    updated = updated.filter((y) => y !== year);
  } else {
    updated.push(year);
  }

  // if nothing selected → fallback to All
  if (updated.length === 0) {
    updated = ["All"];
  }

  setYearFilter(updated);
};

const getYearLabel = () => {
  if (yearFilter.includes("All")) return "All Years";

  const selectedYears = [...yearFilter]
    .filter((y) => y !== "All")
    .sort((a, b) => b - a);

  if (selectedYears.length === 1) return String(selectedYears[0]);
  if (selectedYears.length === 2) {
    return `${selectedYears[0]}, ${selectedYears[1]}`;
  }

  return `${selectedYears.length} opt selected`;
};

  const availableYears = Array.from(
    new Set(
      categorizedReviews.map((review) => moment(review[3], "DD/MM/YYYY").year())
    )
  ).sort((a, b) => b - a); // sorted descending

  const currentYear = moment().year();
const previousYear = currentYear - 1;

const hasCurrentYear = availableYears.includes(currentYear);
const hasPreviousYear = availableYears.includes(previousYear);

let defaultYears = [];

if (hasCurrentYear || hasPreviousYear) {
  defaultYears = [
    ...(hasCurrentYear ? [currentYear] : []),
    ...(hasPreviousYear ? [previousYear] : []),
  ];
} else {
  defaultYears = ["All"];
}

const [yearFilter, setYearFilter] = useState(defaultYears);


  const yearFilteredReviews = categorizedReviews.filter((review) => {
    const reviewYear = moment(review[3], "DD/MM/YYYY").year();
    return (
  yearFilter.includes("All") ||
  yearFilter.includes(reviewYear)
);
  });

  const availableReviewYears = Array.from(
    new Set(
      categorizedReviews.map((review) => moment(review[3], "DD/MM/YYYY").year())
    )
  ).sort((a, b) => b - a);

  const availableProductCodes = Array.from(
    new Set(
      categorizedReviews.map((review) => review[6]?.trim()).filter(Boolean)
    )
  ).sort();


const availableOptionCodes = Array.from(
  new Set(
    categorizedReviews
      .map((r) => (r[8] ? String(r[8]).trim() : ""))
      .filter(
        (opt) =>
          opt &&
          opt !== "?" &&
          opt !== "-" &&
          ProductCodesWithOptions.some((main) => opt.startsWith(main))
      )
  )
).sort();


const combinedCodes = ["All", ...availableProductCodes, ...availableOptionCodes];


  const reviewsForYear = yearFilteredReviews;

  const averageRating =
    reviewsForYear.length > 0
      ? reviewsForYear.reduce(
          (sum, review) => sum + Number(review[4] || 0),
          0
        ) / reviewsForYear.length
      : 0;

  // Calculate average for different product codes
  const productAverages = {};

yearFilteredReviews.forEach((review) => {
  const mainCode = String(review[6] ?? "").trim();
  const optionCode = String(review[8] ?? "").trim();
  let language = String(review[7] ?? "").trim();
  const rating = Number(review[4]) || 0;

  if (!mainCode) return;

  // Format language
  if (language) {
    language =
      language.charAt(0).toUpperCase() + language.slice(1).toLowerCase();
  }

  // Helper to add to productAverages
  const addToAverage = (code) => {
    if (!code) return;

    if (!productAverages[code]) {
      productAverages[code] = { sum: 0, count: 0, languages: {} };
    }

    productAverages[code].sum += rating;
    productAverages[code].count += 1;

    if (language) {
      if (!productAverages[code].languages[language]) {
        productAverages[code].languages[language] = { sum: 0, count: 0 };
      }
      productAverages[code].languages[language].sum += rating;
      productAverages[code].languages[language].count += 1;
    }
  };

  // Always add main product
  addToAverage(mainCode);

  // If product supports options, add option as separate product
  if (
    ProductCodesWithOptions.includes(mainCode) &&
    optionCode &&
    optionCode !== "?" &&
    optionCode !== "-"
  ) {
    addToAverage(optionCode);
  }
});

// Build final averages array
const finalAverages = {};
Object.entries(productAverages).forEach(([code, data]) => {
  finalAverages[code] = {
    overall: (data.sum / data.count).toFixed(2),
    languages: {},
  };

  Object.entries(data.languages).forEach(([lang, langData]) => {
    finalAverages[code].languages[lang] = (
      langData.sum / langData.count
    ).toFixed(2);
  });
});

  // Last month average calculation
  const currentDate = moment();
  const lastMonthDate = moment().subtract(1, "month"); // This gives us the last month
  const lastMonth = lastMonthDate.month(); // Last month as a zero-based index
  const lastMonthYear = lastMonthDate.year(); // Get the year for the last month

  const lastMonthReviews = categorizedReviews.filter((review) => {
    const reviewDate = moment(review[3], "DD/MM/YYYY");
    return (
      reviewDate.month() === lastMonth && reviewDate.year() === lastMonthYear
    );
  });

  const lastMonthAverage =
    lastMonthReviews.length > 0
      ? lastMonthReviews.reduce(
          (sum, review) => sum + (Number(review[4]) || 0),
          0
        ) / lastMonthReviews.length
      : 0;

  const getStarCounts = (reviews) => {
    const counts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach((review) => {
      const rating = Math.round(review[4]);
      if (counts[rating] !== undefined) {
        counts[rating] += 1;
      }
    });
    return counts;
  };


  // Unique languages excluding "?"
  const uniqueLanguages = Array.from(
    new Set(
      categorizedReviews
        .flatMap((review) =>
          review[7] ? review[7].split(",").map((lang) => cleanName(lang)) : []
        )
        .filter((lang) => lang && lang !== "?") // Exclude "?"
    )
  );

  // Average per language excluding "?"
  const languageAverages = {};
  yearFilteredReviews.forEach((review) => {
    const language = cleanName(review[7]);
    if (language && language !== "?") {
      // Exclude "?" from language averages
      if (!languageAverages[language]) {
        languageAverages[language] = { sum: 0, count: 0 };
      }
      languageAverages[language].sum += Number(review[4]) || 0;
      languageAverages[language].count += 1;
    }
  });

  // Apply filters
  let filteredReviews = categorizedReviews.filter((review) => {
    const reviewDate = moment(review[3], "DD/MM/YYYY"); // reviews are in DD/MM/YYYY
    const fromDateMoment = fromDate ? moment(fromDate, "YYYY-MM-DD") : null; // date input format
    const toDateMoment = toDate ? moment(toDate, "YYYY-MM-DD") : null;
    const reviewYear = moment(review[3], "DD/MM/YYYY").year();
    const matchesReviewYear =
      reviewYearFilter === "All" || reviewYear.toString() === reviewYearFilter;

    const productCode = review[6]?.trim();
    const optionCode = review[8] ? String(review[8]).trim() : "";

const matchesProductCode =
  productCodeFilter === "All" ||
  productCode === productCodeFilter ||
  optionCode === productCodeFilter;


    if (!reviewDate.isValid()) return false; // prevent invalid dates from breaking

    const isWithinDateRange =
      (!fromDateMoment || reviewDate.isSameOrAfter(fromDateMoment, "day")) &&
      (!toDateMoment || reviewDate.isSameOrBefore(toDateMoment, "day"));

    const matchesLanguage =
      languageFilter === "All" ||
      (review[7] &&
        review[7].trim() !== "" &&
        review[7]
          .split(",")
          .map((lang) => cleanName(lang).trim())
          .includes(cleanName(languageFilter).trim()));

    const matchesStarRating = starRatingFilter
      ? Math.round(review[4]) === parseInt(starRatingFilter)
      : true;

    return (
      isWithinDateRange &&
      matchesLanguage &&
      matchesStarRating &&
      matchesReviewYear &&
      matchesProductCode
    );
  });

  // Sort reviews by latest date
  filteredReviews.sort(
    (a, b) => moment(b[3], "DD/MM/YYYY") - moment(a[3], "DD/MM/YYYY")
  );

  // Pagination
  const indexOfLastReview = currentPage * reviewsPerPage;
  const indexOfFirstReview = indexOfLastReview - reviewsPerPage;
  const currentReviews = filteredReviews.slice(
    indexOfFirstReview,
    indexOfLastReview
  );

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Render pagination
  const totalPages = Math.ceil(filteredReviews.length / reviewsPerPage);

  const starCounts = getStarCounts(yearFilteredReviews);
  const totalReviews = yearFilteredReviews.length;

  return (
    <div className="guide-profile">
      <div className="guide-profile-header">
        <div className="guide-data">
          <div className="guide-data-head">
            <div className="guide-header-data">
              <h2>Profile for Guide</h2>
              <h3>{guideName}</h3>
              <div className="guide-contact">
                <div className="guide-contactdetails">
                  Phone number - <span>{matchingGuide?.["Phone number"]}</span>
                </div>
                <div className="guide-contactdetails">
                  Email id - <span>{matchingGuide?.Email}</span>
                </div>
              </div>
            </div>
           <div className="guide-year-dropdown" ref={yearDropdownRef}>
  <div
    className="guide-year-dropdown-header"
    onClick={() => setIsYearDropdownOpen((prev) => !prev)}
  >
    <span>{getYearLabel()}</span>
    <span className={`guide-year-dropdown-arrow ${isYearDropdownOpen ? "open" : ""}`}>
      ▾
    </span>
  </div>

  {isYearDropdownOpen && (
    <div className="guide-year-dropdown-menu">
      {availableYears.map((year) => (
        <label key={year} className="guide-year-dropdown-item">
          <span>{year}</span>
          <input
            type="checkbox"
            checked={yearFilter.includes(year)}
            onChange={() => toggleYear(year)}
          />
        </label>
      ))}

      <label className="guide-year-dropdown-item">
        <span>All Years</span>
        <input
          type="checkbox"
          checked={yearFilter.includes("All")}
          onChange={() => toggleYear("All")}
        />
      </label>
    </div>
  )}
</div>
          </div>
          <div>
            <div className="guide-data-heading">
              <div>Tours Provided by the guide</div>
              <div className="guide-data-languages">
                {Object.keys(productAverages).map((code) => (
                  <p key={code}>{code},</p>
                ))}
              </div>  
            </div>
            <div className="guide-data-heading">
              <div>Languages</div>
              <div className="guide-data-languages">
                {Object.keys(languageAverages).map((language) => (
                  <p key={language}>{language}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="average-rating-section">
          <div className="average-rating">
            <div className="rating-average-container">
              <span className="rating-star">★</span>
              <span className="rating-text">
                {averageRating.toFixed(2)} <span>Total Average Rating</span>
              </span>
            </div>
            <p className="basedon">
              <span className="gray-text">Based on -</span> {totalReviews}{" "}
              reviews
            </p>
          </div>

          <div className="rating-breakdown">
            {Object.keys(starCounts).map((star) => (
              <div key={star} className="rating-breakdown-item">
                <span>{star} star</span>
                <div className="rating-bar">
                  <div
                    className="rating-bar-fill"
                    style={{
                      width: `${(starCounts[star] / totalReviews) * 100}%`,
                    }}
                  ></div>
                </div>
                <span>{starCounts[star]}</span>
              </div>
            ))}
          </div>
          <h5 className="collectiveavg">
            Collective Average for Previous Month:{" "}
            {lastMonthReviews.length === 0 ? (
              "No data available"
            ) : (
              <>
                <span className="review-count">
                  {lastMonthAverage.toFixed(2)} Stars{" "}
                </span>
                <span className="review-star-count">
                  ({lastMonthReviews.length} reviews)
                </span>
              </>
            )}
          </h5>
        </div>
      </div>
      <div className="guide-details">
        <div className="guide-section-product">
          <div className="guide-section-product-language">
            <h4>Average Ratings by Products</h4>
          </div>
          <div className="guide-section-product-block">
            {Object.keys(finalAverages).map((code) => (
              <div key={code} className="product-block">
                {/* Product overall */}
                <p>
                  <span className="gray-text-code">{code}:</span>
                  <span className="black-text-rating-main">
                    {" "}
                    {finalAverages[code].overall}
                  </span>
                  <span className="black-text-review-count">
                    ({productAverages[code].count} reviews)
                  </span>
                </p>

                {/* Languages for this product */}
                <ul>
                  {Object.keys(finalAverages[code].languages).map((lang) => (
                    <li key={lang}>
                      <span className="black-text-lang">{lang}:</span>
                      <span className="black-text-rating">
                        {" "}
                        {finalAverages[code].languages[lang]}
                      </span>
                      <span className="black-text-review-count">
                        ({productAverages[code].languages[lang].count} reviews)
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
        <div className="guide-section-language">
          <h4>Ratings by Languages</h4>
          {Object.keys(languageAverages).map((language) => (
            <p key={language}>
              <span className="gray-text-code">{language}:</span>
              <span className="black-text-lang-rating">
                {" "}
                {(
                  languageAverages[language].sum /
                  languageAverages[language].count
                ).toFixed(2)}{" "}
                Stars
              </span>
              <span className="black-text-review-count">
                ({languageAverages[language].count} reviews)
              </span>
            </p>
          ))}
        </div>
      </div>

      <div className="guide-profile-graph">
        <GuideProfileGraph reviews={categorizedReviews} />
      </div>
      <div className="three-month-graph">
        <ThreeMonthGuide reviews={categorizedReviews} />
      </div>
      <div className="guide-review-container">
        <h4 className="guide-review">
          Reviews for Guide:{" "}
          <span className="guide-review-name">{guideName}</span>
        </h4>
        <div className="guide-review-filter">
          <label>
            From Date:
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </label>
          <label>
            To Date:
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </label>
          <label>
  Product Code:
  <select
    value={productCodeFilter}
    onChange={(e) => setProductCodeFilter(e.target.value)}
  >
    {combinedCodes.map((code) => (
      <option key={code} value={code}>
        {code}
      </option>
    ))}
  </select>
</label>
          <label>
            Language:
            <select
              value={languageFilter}
              onChange={(e) => setLanguageFilter(e.target.value)}
            >
              <option value="All">All</option>
              {uniqueLanguages.map((lang) => (
                <option key={lang} value={lang}>
                  {lang}
                </option>
              ))}
            </select>
          </label>
          <label>
            Star Rating:
            <select
              value={starRatingFilter}
              onChange={(e) => setStarRatingFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="5">5 Stars</option>
              <option value="4">4 Stars</option>
              <option value="3">3 Stars</option>
              <option value="2">2 Stars</option>
              <option value="1">1 Star</option>
            </select>
          </label>
          <label>
            Year:
            <select
              value={reviewYearFilter}
              onChange={(e) => setReviewYearFilter(e.target.value)}
            >
              <option value="All">All</option>
              {availableReviewYears.map((year) => (
                <option key={year} value={year.toString()}>
                  {year}
                </option>
              ))}
            </select>
          </label>
        </div>
        {currentReviews.length > 0 ? (
          currentReviews.map((review, index) => (
            <div key={index} className="guidereview-card">
              <div className="guidereview-header">
                <div className="guidereview-id-container">
                  <span className="guidereview-id">{review[1]}</span>
                  <span className="guidereview-date">
                    Tour Date: {review[2]}
                  </span>
                </div>
                \<div className="guidereview-star-container">
  <span className="guidestars">{starIcons(review[4])}</span>

  <div className="guideproduct-code-wrapper">
    <span className="guideproduct-code">
      {review[6]}
    </span>
    {review[8] && String(review[8]).trim() !== "" && (
      <span className="guideproduct-option">
        {String(review[8])}
      </span>
    )}
  </div>
</div>
              </div>
              <p className="guidecomment">{review[5]}</p>
              <div className="guidereview-footer">
                <span className="guidedate">Comment Date: {review[3]}</span>
                <span className="guideguide-name">
                  <div className="guideguide-heading">Language:</div>
                  {review[7]}
                </span>
              </div>
            </div>
          ))
        ) : (
          <p>No reviews available for this guide.</p>
        )}
        <div className="pagination">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            &laquo;
          </button>
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            &lt;
          </button>
          {currentPage > 3 && <span>...</span>} {/* Optional left ellipsis */}
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            const pageNum = currentPage - 2 + i;
            if (pageNum < 1 || pageNum > totalPages) return null;
            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={currentPage === pageNum ? "active" : ""}
              >
                {pageNum}
              </button>
            );
          })}
          {currentPage < totalPages - 2 && <span>...</span>}{" "}
          {/* Optional right ellipsis */}
          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
          >
            &gt;
          </button>
          <button
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            &raquo;
          </button>
        </div>
      </div>
    </div>
  );
};

export default GuideProfile;
